import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@d-m-organization/hub-shared-lib/auth";
import { LogoMark } from "./LogoMark.js";

interface HubLayoutProps {
  children: React.ReactNode;
  /** If true, renders without the nav header (used by PublicLanding which has its own nav) */
  bare?: boolean;
}

/**
 * Authenticated dashboard layout with brand header.
 * Public landing page uses bare={true} and provides its own nav.
 */
export function HubLayout({ children, bare }: HubLayoutProps) {
  const { isAuthed, userLabel, logout } = useAuth();
  const { t } = useTranslation();

  if (bare) {
    return <>{children}</>;
  }

  // Unauthenticated pages (login / signup / auth callbacks) get a minimal
  // brand-only header — no nav tabs, no sign-in button (you're already here) —
  // just enough to show which app you're in.
  if (!isAuthed) {
    return (
      <div style={{ background: "var(--bg)", minHeight: "100vh" }}>
        <header style={{ background: "#fff", borderBottom: "1px solid var(--muted)" }}>
          <div
            style={{
              maxWidth: 1120,
              margin: "0 auto",
              padding: "0 24px",
              height: 60,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
              <LogoMark color="var(--purple)" size={22} />
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 800,
                  color: "var(--navy)",
                  letterSpacing: "-0.02em",
                }}
              >
                {t("hubLayout.brand")}
              </span>
            </Link>
          </div>
        </header>
        <main>{children}</main>
      </div>
    );
  }

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }}>
      {/* ── Header ──────────────────────────────────────────────── */}
      <header style={{ background: "#fff", borderBottom: "1px solid var(--muted)" }}>
        <div
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            padding: "0 24px",
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <LogoMark color="var(--purple)" size={22} />
            <span
              style={{
                fontSize: 16,
                fontWeight: 800,
                color: "var(--navy)",
                letterSpacing: "-0.02em",
              }}
            >
              {t("hubLayout.brand")}
            </span>
          </Link>

          {/* Nav tabs (Dashboard / Pricing / Billing) removed for the release —
              not needed yet. Re-add this <nav> when they belong in the header. */}

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {isAuthed ? (
              <>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: "var(--purple-light)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 14,
                    fontWeight: 700,
                    color: "var(--purple)",
                  }}
                >
                  {userLabel}
                </div>
                <button
                  type="button"
                  onClick={() => void logout()}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 8,
                    fontSize: 13,
                    fontWeight: 500,
                    background: "transparent",
                    color: "var(--text-muted)",
                    border: "1px solid var(--muted)",
                    cursor: "pointer",
                  }}
                >
                  {t("hubLayout.signOut")}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="brand-btn brand-btn-sm brand-btn-primary"
              >
                {t("hubLayout.signIn")}
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* ── Content ─────────────────────────────────────────────── */}
      <main>{children}</main>
    </div>
  );
}
